// ============================================
// SERVICE: PROBLEMAS CLÍNICOS
// ============================================
// Funções para gerenciar problemas clínicos via API
// ============================================

import { API_CONFIG } from './staticDataConfig';

const baseURL = API_CONFIG.BASE_URL;

export interface ProblemaClinico {
  id: number;
  codigo: string;
  valor: string;
  descricao?: string;
  categoria?: string;
  cid10?: string;
  dsm5?: string;
  ativo: boolean;
  ordem: number;
}

class ProblemasClinicosService {
  private getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Buscar todos os problemas clínicos (ativos ou incluindo inativos)
  async obterTodos(incluirInativos: boolean = false): Promise<ProblemaClinico[]> {
    try {
      const url = incluirInativos 
        ? `${baseURL}/problemas-clinicos?incluirInativos=true`
        : `${baseURL}/problemas-clinicos`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar problemas clínicos: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log(`[ProblemasClinicosService] Problemas clínicos obtidos: ${data.problemasClinicos?.length || 0} (incluirInativos: ${incluirInativos})`);
      return data.problemasClinicos || [];
    } catch (error) {
      console.error('[ProblemasClinicosService] Erro ao obter problemas clínicos:', error);
      throw error;
    }
  }

  // Adicionar novo problema clínico
  async adicionar(problema: {
    codigo: string;
    valor: string;
    descricao?: string;
    categoria?: string;
    cid10?: string;
    dsm5?: string;
    ordem?: number;
  }): Promise<ProblemaClinico> {
    try {
      const response = await fetch(`${baseURL}/problemas-clinicos`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(problema)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao adicionar problema clínico');
      }

      const data = await response.json();
      return data.problemaClinico;
    } catch (error) {
      console.error('[ProblemasClinicosService] Erro ao adicionar problema clínico:', error);
      throw error;
    }
  }

  // Atualizar problema clínico existente
  async atualizar(id: number, problema: {
    codigo?: string;
    valor?: string;
    descricao?: string;
    categoria?: string;
    cid10?: string;
    dsm5?: string;
    ordem?: number;
    ativo?: boolean;
  }): Promise<ProblemaClinico> {
    try {
      const response = await fetch(`${baseURL}/problemas-clinicos/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(problema)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao atualizar problema clínico');
      }

      const data = await response.json();
      return data.problemaClinico;
    } catch (error) {
      console.error('[ProblemasClinicosService] Erro ao atualizar problema clínico:', error);
      throw error;
    }
  }

  // Excluir problema clínico (soft delete)
  async excluir(id: number): Promise<void> {
    try {
      const response = await fetch(`${baseURL}/problemas-clinicos/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao excluir problema clínico');
      }
    } catch (error) {
      console.error('[ProblemasClinicosService] Erro ao excluir problema clínico:', error);
      throw error;
    }
  }
}

export const problemasClinicosService = new ProblemasClinicosService();
